import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, RotateCcw, Send, Ticket as TicketIcon } from 'lucide-react';
import apiService from '../../services/apiService';
import { useI18n } from '../../utils/i18n';
import MarkdownText from '../ui/MarkdownText';
import './mobil.css';
// Ladepunkte (.ai-chat-dots) wie in der Desktop-Suche
import '../../styles/AiSearch.css';

// KI-Suche für die Mobilansicht: dieselben Aufrufe wie GlobalSearchView
// (#122) -- die erste Frage läuft als volle Suche (GET /api/search/global),
// jede weitere als Rückfrage gegen das Kontextfenster der ersten
// (POST /api/search/ai-followup). Die Trefferkarten stehen als Ticketliste
// unter dem Chat; ein Tipp öffnet das Ticket in der Handy-Detailansicht.
//
// Bewusst nicht dabei: Verläufe speichern, "An Ticket anhängen", Debug.
// Die bleiben am Arbeitsplatz.
const MAX_KARTEN = 10;

const MobilKiSuche = ({ onClose, onOeffneTicket }) => {
  const { t } = useI18n();
  const [eingabe, setEingabe] = useState('');
  const [nachrichten, setNachrichten] = useState([]); // [{ rolle: 'user'|'ki', text }]
  const [kontext, setKontext] = useState('');
  const [karten, setKarten] = useState([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [abgeschaltet, setAbgeschaltet] = useState(false);
  const endeRef = useRef(null);
  const feldRef = useRef(null);

  useEffect(() => {
    try { endeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); } catch (_) {
      // scrollIntoView fehlt nur in sehr alten Browsern
    }
  }, [nachrichten, laeuft]);

  const keineAntwort = t('search.noAnswer', 'Keine Antwort erhalten.');

  const suche = async (frage) => {
    setNachrichten([{ rolle: 'user', text: frage }]);
    setKarten([]);
    setKontext('');
    const res = await apiService.globalSearch(frage);
    setKarten(Array.isArray(res?.results?.cards) ? res.results.cards : []);
    setKontext(res?.ai?.contextText || '');
    setNachrichten(prev => [...prev, { rolle: 'ki', text: res?.ai?.answer || keineAntwort }]);
  };

  const rueckfrage = async (frage) => {
    const erste = nachrichten.find(n => n.rolle === 'user');
    const letzte = [...nachrichten].reverse().find(n => n.rolle === 'ki');
    setNachrichten(prev => [...prev, { rolle: 'user', text: frage }]);
    const res = await apiService.aiFollowup({
      followup: frage,
      originalQuestion: erste?.text || '',
      contextText: kontext,
      previousAnswer: letzte?.text || ''
    });
    setNachrichten(prev => [...prev, { rolle: 'ki', text: res?.answer || keineAntwort }]);
  };

  const absenden = async (e) => {
    e.preventDefault();
    const frage = eingabe.trim();
    // Wie am Desktop: eine Suche braucht mindestens drei Zeichen.
    if (laeuft || frage.length < 3) return;
    setEingabe('');
    setFehler(null);
    setLaeuft(true);
    try {
      if (nachrichten.length === 0 || !kontext) await suche(frage);
      else await rueckfrage(frage);
    } catch (err) {
      if (err?.response?.data?.aiDisabled) setAbgeschaltet(true);
      else setFehler(t('mobile.aiSearchError', 'Die Suche hat nicht geklappt. Bitte noch einmal versuchen.'));
    } finally {
      setLaeuft(false);
    }
  };

  const neueSuche = () => {
    setNachrichten([]);
    setKontext('');
    setKarten([]);
    setFehler(null);
    setEingabe('');
    try { feldRef.current?.focus(); } catch (_) {
      // Fokus ist nur Komfort
    }
  };

  return (
    <div className="mobil-ki" role="dialog" aria-modal="true" aria-label={t('search.title', 'KI-Suche')}>
      <header className="mobil-kopf">
        <button type="button" className="mobil-kopf-knopf" onClick={onClose}
          aria-label={t('mobile.back', 'Zurück')} title={t('mobile.back', 'Zurück')}>
          <ArrowLeft size={24} />
        </button>
        <h1>{t('search.title', 'KI-Suche')}</h1>
        {nachrichten.length > 0 && (
          <button type="button" className="mobil-kopf-knopf" onClick={neueSuche}
            aria-label={t('mobile.newSearch', 'Neue Suche')} title={t('mobile.newSearch', 'Neue Suche')}>
            <RotateCcw size={22} />
          </button>
        )}
      </header>

      <div className="mobil-ki-verlauf">
        {abgeschaltet ? (
          <p className="mobil-leer">{t('mobile.aiSearchDisabled', 'Die KI-Suche ist auf dieser Installation abgeschaltet.')}</p>
        ) : (
          <>
            {nachrichten.length === 0 && !laeuft && (
              <p className="mobil-ki-hinweis">{t('search.emptyHint', 'Stelle eine Frage — die KI durchsucht Tickets, Kommentare und E-Mails.')}</p>
            )}
            {nachrichten.map((n, i) => (
              <div key={i} className={`mobil-ki-blase ${n.rolle === 'user' ? 'frage' : 'antwort'}`}>
                {n.rolle === 'user' ? n.text : <MarkdownText>{n.text}</MarkdownText>}
              </div>
            ))}
            {laeuft && (
              <div className="mobil-ki-blase antwort" role="status" aria-label={t('mobile.aiSearching', 'Suche läuft…')}>
                <div className="ai-chat-dots" aria-hidden="true">
                  <span className="ai-chat-dot" /><span className="ai-chat-dot" /><span className="ai-chat-dot" />
                </div>
              </div>
            )}
            {fehler && <p className="mobil-ki-fehler" role="alert">{fehler}</p>}
            {karten.length > 0 && (
              <section className="mobil-ki-karten" aria-label={t('mobile.aiFoundTickets', 'Gefundene Tickets')}>
                <h2 className="mobil-label">{t('mobile.aiFoundTickets', 'Gefundene Tickets')}</h2>
                {karten.slice(0, MAX_KARTEN).map(k => (
                  <button key={k.ticketId} type="button" className="mobil-ki-karte" onClick={() => onOeffneTicket({ id: k.ticketId })}>
                    <TicketIcon size={18} aria-hidden="true" />
                    <span className="mobil-ki-karte-text">
                      <span className="mobil-ki-karte-nummer">{k.ticketNumber}</span>
                      <span className="mobil-ki-karte-titel">{k.title}</span>
                    </span>
                  </button>
                ))}
              </section>
            )}
          </>
        )}
        <div ref={endeRef} />
      </div>

      {!abgeschaltet && (
        <form className="mobil-ki-eingabe" onSubmit={absenden}>
          <input
            ref={feldRef}
            type="text"
            value={eingabe}
            onChange={(e) => setEingabe(e.target.value)}
            placeholder={nachrichten.length === 0
              ? t('search.placeholder', 'Was liegt dir auf dem Herzen?')
              : t('search.ai.followup.placeholder', 'Rückfrage stellen…')}
            aria-label={t('mobile.aiQuestion', 'Frage an die KI')}
            enterKeyHint="send"
            disabled={laeuft}
          />
          <button type="submit" className="mobil-ki-senden" disabled={laeuft || eingabe.trim().length < 3}
            aria-label={t('mobile.send', 'Senden')} title={t('mobile.send', 'Senden')}>
            <Send size={20} />
          </button>
        </form>
      )}
    </div>
  );
};

export default MobilKiSuche;
